import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router';
import config from '../utils/config';
import Button from '../components/ui/Button';
import AuthPageLayout from '../components/AuthPageLayout';
import { Input } from '../components/pilot-ui/input';
import { Label } from '../components/pilot-ui/label';

interface LoginResponse {
  token: string;
}

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const resetSuccess = searchParams.get('reset') === 'success';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const response = await axios.post<LoginResponse>(
        `${config.accountsApiUrl}/accounts/login`,
        { email, password }
      );

      const { token } = response.data;
      login(token);

      const from = (location.state as { from?: string } | null)?.from || '/devices';
      navigate(from, { replace: true });
    } catch (err) {
      setError(axios.isAxiosError<{ message?: string }>(err)
        ? err.response?.data?.message || 'Login failed. Please try again.'
        : 'Login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthPageLayout title="Login">
      {resetSuccess && !error && (
        <p className="mb-4 rounded-md border border-green-300 bg-green-50 px-4 py-2 text-green-800" role="status">
          Password set — please log in.
        </p>
      )}
      {error && <p id="login-error" className="mb-4 text-sm text-red-700" role="alert">{error}</p>}
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <Label htmlFor="login-email">Email</Label>
          <p id="login-email-help" className="mb-2 text-sm text-muted-foreground">Your console account email address.</p>
          <Input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="username"
            aria-describedby={`login-email-help${error ? ' login-error' : ''}`}
            aria-invalid={error ? true : undefined}
          />
        </div>
        <div className="mb-4">
          <Label htmlFor="login-password">Password</Label>
          <p id="login-password-help" className="mb-2 text-sm text-muted-foreground">The password for this console account.</p>
          <Input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            aria-describedby={`login-password-help${error ? ' login-error' : ''}`}
            aria-invalid={error ? true : undefined}
          />
        </div>
        <Button
          variant="primary"
          type="submit"
          className="w-full"
          disabled={submitting}
          title="Sign in to the console with this email and password"
        >
          {submitting ? 'Signing in…' : 'Login'}
        </Button>
        <div className="mt-3">
          <Link to="/forgot-password" className="text-sm text-blue-700 underline" title="Request a password reset link by email">
            Forgot password?
          </Link>
        </div>
      </form>
    </AuthPageLayout>
  );
};

export default LoginPage;
